import type { PointHistoryEntry } from '@/components/Pages/home/components/DialogPointHistory'
import type { StampDetailInfo } from '@/components/Pages/home/components/DialogStampDetail'
import { mergeRewardsWithUserData } from '@/core/api/endpoints/rewards'
import { ApiError } from '@/core/api/request'
import {
  useTanstackRewardsFetchFn,
  useTanstackSheetsFetchFn,
  useTanstackUserRewardsFetchFn,
  useTanstackUserSheetsFetchFn,
} from '@/core/api/useTanstackFetchFn'
import { is401Error } from '@/core/api/xstateUtils'
import { PAGE_PATH, logger } from '@/core/app/logger'
import type {
  MergedRewardData,
  SheetListResponse,
  UserRewardStatusListResponse,
  UserSheetListResponse,
} from '@/schemas/v2'
import { useQueryClient } from '@tanstack/react-query'
import { useMachine } from '@xstate/react'
import { useMemo, useRef } from 'react'
import { assign, fromPromise, setup } from 'xstate'
import type { ErrorActorEvent } from 'xstate'

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

type HomeInput = {
  /** HOME画面でのステートマシン実行前の初期値 */
  isAuthenticated: boolean
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * ゲスト向けシート一覧（stamps含む）とユーザー向けシート一覧（meta/history含む）をマージする。
/**
 * ゲスト向けシート一覧（stamps含む）とユーザー向けシート一覧（meta/history含む）をマージする。
 *
 * GET /api/v2/user/sheets は stamps を含まないため、
 * GET /api/v2/sheets から stamps を取得してユーザーシートに補完する。
 */
function mergeSheetsWithUserData(
  sheetsResult: SheetListResponse,
  userSheetsResult: UserSheetListResponse,
): UserSheetListResponse {
  const userSheetsMap = new Map(userSheetsResult.sheets.map((s) => [s.id, s]))
  return {
    sheets: sheetsResult.sheets.map((sheet) => {
      const userSheet = userSheetsMap.get(sheet.id)
      return {
        ...sheet,
        meta: userSheet?.meta ?? null,
        history: userSheet?.history ?? [],
        // 購入連動ロック（locked/unlock）はユーザー個別の解放状態が正。ユーザーシートがあれば上書きする
        ...(userSheet ? { locked: userSheet.locked, unlock: userSheet.unlock } : {}),
      }
    }),
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

type HomeContext = {
  /** スタンプ詳細ダイアログで表示中のスタンプ（stampDetail 以外では null）*/
  selectedStamp: StampDetailInfo | null
  isAuthenticated: boolean
  /** 認証済み: UserSheetListResponse / 未認証: SheetListResponse */
  sheetsData: UserSheetListResponse | SheetListResponse | undefined
  /** rewards API のレスポンス（ロード完了後に格納）。常に MergedRewardData ベース */
  rewardsData: { rewards: MergedRewardData[] } | undefined
  /** ポイント履歴ダイアログに表示する履歴アイテム */
  pointHistoryItems: PointHistoryEntry[]
  /** 履歴ダイアログの種別（'point': ポイントラリー / 'stamp': ポイントカードの獲得履歴・#2970） */
  pointHistoryVariant: 'point' | 'stamp'
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

type HomeEvent =
  | { type: 'OPEN_SUMMARY' }
  | { type: 'OPEN_HOW_TO_PLAY' }
  | { type: 'OPEN_STAMP_DETAIL'; stamp: StampDetailInfo }
  | { type: 'OPEN_POINT_HISTORY'; items: PointHistoryEntry[]; variant?: 'point' | 'stamp' }
  /** ready サブ状態のダイアログを閉じる（selectedStamp もクリア） */
  | { type: 'CLOSE' }
  /** sheets フェッチ失敗後に loading からやり直す */
  | { type: 'RETRY' }
  /** トークンリフレッシュ完了後に loading からやり直す（401 エラー時） */
  | { type: 'REFRESH_COMPLETE' }

// ---------------------------------------------------------------------------
// Machine
// ---------------------------------------------------------------------------

const homeStateMachineBase = setup({
  types: {
    context: {} as HomeContext,
    events: {} as HomeEvent,
    input: {} as HomeInput,
  },
  actors: {
    fetchSheets: fromPromise<UserSheetListResponse | SheetListResponse | undefined, void>(async () => undefined),
    fetchRewards: fromPromise<{ rewards: MergedRewardData[] } | undefined, void>(async () => undefined),
  },
  guards: {
    is401Error: ({ event }) => is401Error((event as unknown as ErrorActorEvent).error),
  },
}).createMachine({
  id: 'home',
  initial: 'loading',
  context: ({ input }) => ({
    selectedStamp: null,
    isAuthenticated: input.isAuthenticated,
    sheetsData: undefined,
    rewardsData: undefined,
    pointHistoryItems: [],
    pointHistoryVariant: 'point',
  }),
  states: {
    /**
     * 初期ローディング状態
     * sheets と rewards を並列フェッチし、全て完了したら ready 状態へ遷移する。
     *
     * 初回アンケート・確認モーダルの表示制御は RaundPreRenderingContext が担当するため、
     * このステートマシンでは管理しない。
     */
    loading: {
      initial: 'initialFetching',
      states: {
        initialFetching: {
          type: 'parallel' as const,
          states: {
            sheets: {
              initial: 'fetching',
              states: {
                fetching: {
                  invoke: {
                    src: 'fetchSheets',
                    onDone: {
                      target: 'done',
                      actions: assign({
                        sheetsData: ({ event }) => event.output,
                      }),
                    },
                    onError: [{ guard: 'is401Error', target: '#home.waitingForRefresh' }, { target: '#home.error' }],
                  },
                },
                done: { type: 'final' as const },
              },
            },
            rewards: {
              initial: 'fetching',
              states: {
                fetching: {
                  invoke: {
                    src: 'fetchRewards',
                    onDone: {
                      target: 'done',
                      actions: assign({
                        rewardsData: ({ event }) => event.output,
                      }),
                    },
                    onError: { target: 'done' }, // 取得失敗はスキップして HOME 画面を表示継続
                  },
                },
                done: { type: 'final' as const },
              },
            },
          },
          onDone: { target: '#home.ready' },
        },
      },
    },

    /**
     * 読み込み完了状態
     * ここを起点として各ダイアログ状態へ遷移する。
     */
    ready: {
      initial: 'idle',
      states: {
        /** 通常表示（ダイアログ非表示） */
        idle: {
          on: {
            OPEN_SUMMARY: 'campaignDescription',
            OPEN_HOW_TO_PLAY: 'howToPlay',
            OPEN_STAMP_DETAIL: {
              target: 'stampDetail',
              actions: assign({
                selectedStamp: ({ event }) => (event.type === 'OPEN_STAMP_DETAIL' ? event.stamp : null),
              }),
            },
            OPEN_POINT_HISTORY: {
              target: 'pointHistory',
              actions: assign({
                pointHistoryItems: ({ event }) => event.items,
                pointHistoryVariant: ({ event }) => event.variant ?? 'point',
              }),
            },
          },
        },

        /** 概要ダイアログを開いた状態 */
        campaignDescription: {
          on: { CLOSE: 'idle' },
        },

        /** 遊び方ダイアログを開いた状態 */
        howToPlay: {
          on: { CLOSE: 'idle' },
        },

        /**
         * スタンプ詳細を開いた状態
         * 選択中のスタンプは context.selectedStamp で管理し、
         * 閉じる時にクリアする。
         */
        stampDetail: {
          on: {
            CLOSE: {
              target: 'idle',
              actions: assign({ selectedStamp: null }),
            },
          },
        },

        /** ポイント履歴ダイアログを開いた状態 */
        pointHistory: {
          on: { CLOSE: 'idle' },
        },
      },
    },

    /**
     * エラー状態（sheets フェッチ失敗時）
     * RETRY イベントで loading からやり直す。
     */
    error: {
      on: { RETRY: 'loading' },
    },

    /**
     * 401 エラー発生時のトークンリフレッシュ待機状態。
     * AppMachineContext の tokenRefresh が waiting になったら REFRESH_COMPLETE を受信し loading へ遷移する。
     * 30 秒以内に REFRESH_COMPLETE が届かない場合は error ステートへタイムアウトする。
     */
    waitingForRefresh: {
      after: { 10000: '#home.error' },
      on: { REFRESH_COMPLETE: 'loading' },
    },
  },
})

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * ホームページ用 XState ステートマシン hook。
 *
 * loading 状態で sheets / rewards を並列フェッチし、完了後に ready 状態へ遷移する。
 * 初回アンケート・確認モーダルの表示制御は RaundPreRenderingContext が担当する。
 *
 * 状態遷移:
 *   loading (initialFetching: parallel)
 *     ├─ sheets:      fetching → done
 *     └─ rewards:     fetching → done
 *          ↓
 *   ready
 *     ├─ idle
 *     ├─ campaignDescription
 *     ├─ howToPlay
 *     ├─ stampDetail
 *     └─ pointHistory
 *
 * @example
 * ```tsx
 * const { isLoading, sheetsData, rewardsData, ... } = useHomeStateMachine({ input })
 * ```
 */
export const useHomeStateMachine = ({ input }: { input: HomeInput }) => {
  const queryClient = useQueryClient()
  const sheetsQuery = useTanstackSheetsFetchFn()
  const userSheetsQuery = useTanstackUserSheetsFetchFn()
  const rewardsQuery = useTanstackRewardsFetchFn()
  const userRewardsQuery = useTanstackUserRewardsFetchFn()

  const queryClientRef = useRef(queryClient)
  const sheetsQueryRef = useRef(sheetsQuery)
  const userSheetsQueryRef = useRef(userSheetsQuery)
  const rewardsQueryRef = useRef(rewardsQuery)
  const userRewardsQueryRef = useRef(userRewardsQuery)
  queryClientRef.current = queryClient
  sheetsQueryRef.current = sheetsQuery
  userSheetsQueryRef.current = userSheetsQuery
  rewardsQueryRef.current = rewardsQuery
  userRewardsQueryRef.current = userRewardsQuery

  // actors は ref 経由で最新値を参照するため、依存配列は再生成が必要な値のみに絞っている
  const machine = useMemo(
    () =>
      homeStateMachineBase.provide({
        actors: {
          fetchSheets: fromPromise(async () => {
            const logBase = { page: PAGE_PATH.home, xstate: 'home' } as const
            try {
              if (input.isAuthenticated) {
                logger.info({ ...logBase, msg: 'fetchSheets (authenticated) 開始' })
                const [sheetsResult, userSheetsResult] = await Promise.all([
                  queryClientRef.current.fetchQuery(sheetsQueryRef.current),
                  queryClientRef.current.fetchQuery(userSheetsQueryRef.current),
                ])
                logger.info({ ...logBase, msg: 'fetchSheets (authenticated) 完了' })
                return mergeSheetsWithUserData(sheetsResult ?? { sheets: [] }, userSheetsResult ?? { sheets: [] })
              }
              logger.info({ ...logBase, msg: 'fetchSheets (guest) 開始' })
              const result = await queryClientRef.current.fetchQuery(sheetsQueryRef.current)
              logger.info({ ...logBase, msg: 'fetchSheets (guest) 完了' })
              return result
            } catch (e) {
              logger.error({ ...logBase, msg: `fetchSheets エラー: ${String(e)}` })
              throw e
            }
          }),
          fetchRewards: fromPromise<{ rewards: MergedRewardData[] } | undefined, void>(async () => {
            const logBase = { page: PAGE_PATH.home, xstate: 'home' } as const
            try {
              logger.info({ ...logBase, msg: 'fetchRewards 開始' })
              const rewardsResult = await queryClientRef.current.fetchQuery(rewardsQueryRef.current)
              if (input.isAuthenticated) {
                const userRewardsResult = await queryClientRef.current
                  .fetchQuery(userRewardsQueryRef.current)
                  .catch((e: unknown) => {
                    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
                      return { rewards: [] as UserRewardStatusListResponse['rewards'] }
                    }
                    throw e
                  })
                logger.info({ ...logBase, msg: 'fetchRewards (authenticated) 完了' })
                return mergeRewardsWithUserData(rewardsResult ?? { rewards: [] }, userRewardsResult ?? { rewards: [] })
              }
              logger.info({ ...logBase, msg: 'fetchRewards (guest) 完了' })
              return mergeRewardsWithUserData(rewardsResult ?? { rewards: [] }, { rewards: [] })
            } catch (e) {
              logger.error({ ...logBase, msg: `fetchRewards エラー: ${String(e)}` })
              throw e
            }
          }),
        },
      }),
    [input.isAuthenticated],
  )

  const [state, send] = useMachine(machine, { input })

  return {
    /** XState state オブジェクト（高度な判定に使用） */
    state,
    /** イベント送出関数 */
    send,
    // --- ローディング ---
    isLoading: state.matches('loading') || state.matches('waitingForRefresh'),
    // --- エラー（sheets フェッチ失敗） ---
    isError: state.matches('error'),
    // --- 読み込み完了 ---
    isReady: state.matches('ready'),
    // --- ready サブ状態（ダイアログ open/close） ---
    isSummaryOpen: state.matches({ ready: 'campaignDescription' }),
    isHowToPlayOpen: state.matches({ ready: 'howToPlay' }),
    isStampDetailOpen: state.matches({ ready: 'stampDetail' }),
    isPointHistoryOpen: state.matches({ ready: 'pointHistory' }),
    // --- context ---
    /** 選択中のスタンプ（stampDetail 時のみ非 null） */
    selectedStamp: state.context.selectedStamp,
    /** sheets API レスポンス（loading 中は undefined） */
    sheetsData: state.context.sheetsData,
    /** rewards API レスポンス（loading 中・取得失敗時は undefined） */
    rewardsData: state.context.rewardsData,
    /** ポイント履歴ダイアログのアイテム一覧 */
    pointHistoryItems: state.context.pointHistoryItems,
    /** 履歴ダイアログの種別（'point': ポイントラリー / 'stamp': ポイントカードの獲得履歴） */
    pointHistoryVariant: state.context.pointHistoryVariant,
  }
}
